'use client'

import * as AlertDialogPrimitive from '@radix-ui/react-alert-dialog'
import * as React from 'react'

import { cn } from '../../utils/cn'
import { Button } from '../_base/Button'

const overlayClasses = 'fixed inset-0 z-50 bg-black/50 backdrop-blur-lg'
const contentClasses =
  'bg-popover p-md fixed top-[50%] left-[50%] z-50 grid w-full max-w-[calc(100%-2rem)] translate-x-[-50%] translate-y-[-50%] gap-[24px] rounded-[20px] border shadow-md outline-none sm:max-w-[300px]'
const titleClasses = 'text-popover-foreground text-base font-medium'
const descriptionClasses = 'text-foreground text-2xs'

function AlertDialog({ ...props }: React.ComponentProps<typeof AlertDialogPrimitive.Root>) {
  return <AlertDialogPrimitive.Root data-slot="alert-dialog" {...props} />
}

function AlertDialogTrigger({
  ...props
}: React.ComponentProps<typeof AlertDialogPrimitive.Trigger>) {
  return <AlertDialogPrimitive.Trigger data-slot="alert-dialog-trigger" {...props} />
}

function AlertDialogPortal({ ...props }: React.ComponentProps<typeof AlertDialogPrimitive.Portal>) {
  return <AlertDialogPrimitive.Portal data-slot="alert-dialog-portal" {...props} />
}

function AlertDialogOverlay({
  className,
  ...props
}: React.ComponentProps<typeof AlertDialogPrimitive.Overlay>) {
  return (
    <AlertDialogPrimitive.Overlay
      data-slot="alert-dialog-overlay"
      className={cn(
        'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
        overlayClasses,
        className
      )}
      {...props}
    />
  )
}

function AlertDialogContent({
  className,
  ...props
}: React.ComponentProps<typeof AlertDialogPrimitive.Content>) {
  return (
    <AlertDialogPortal>
      <AlertDialogOverlay />
      <AlertDialogPrimitive.Content
        data-slot="alert-dialog-content"
        className={cn(
          'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 duration-200',
          contentClasses,
          className
        )}
        {...props}
      />
    </AlertDialogPortal>
  )
}

function AlertDialogHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="alert-dialog-header"
      className={cn('gap-sm flex flex-col items-center text-center', className)}
      {...props}
    />
  )
}

function AlertDialogFooter({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="alert-dialog-footer"
      className={cn('gap-sm flex flex-col', className)}
      {...props}
    />
  )
}

function AlertDialogTitle({
  className,
  ...props
}: React.ComponentProps<typeof AlertDialogPrimitive.Title>) {
  return (
    <AlertDialogPrimitive.Title
      data-slot="alert-dialog-title"
      className={cn(titleClasses, className)}
      {...props}
    />
  )
}

function AlertDialogDescription({
  className,
  ...props
}: React.ComponentProps<typeof AlertDialogPrimitive.Description>) {
  return (
    <AlertDialogPrimitive.Description
      data-slot="alert-dialog-description"
      className={cn(descriptionClasses, className)}
      {...props}
    />
  )
}

function AlertDialogAction({
  className,
  variant = 'destructive',
  size = 'default',
  fullWidth,
  ...props
}: React.ComponentProps<typeof AlertDialogPrimitive.Action> &
  Pick<React.ComponentProps<typeof Button>, 'variant' | 'size' | 'fullWidth'>) {
  return (
    <Button variant={variant} size={size} fullWidth={fullWidth} asChild>
      <AlertDialogPrimitive.Action
        data-slot="alert-dialog-action"
        className={cn(className)}
        {...props}
      />
    </Button>
  )
}

function AlertDialogCancel({
  className,
  variant = 'secondary',
  size = 'default',
  fullWidth,
  ...props
}: React.ComponentProps<typeof AlertDialogPrimitive.Cancel> &
  Pick<React.ComponentProps<typeof Button>, 'variant' | 'size' | 'fullWidth'>) {
  return (
    <Button variant={variant} size={size} fullWidth={fullWidth} asChild>
      <AlertDialogPrimitive.Cancel
        data-slot="alert-dialog-cancel"
        className={cn(className)}
        {...props}
      />
    </Button>
  )
}

/**
 * Non-portal `AlertDialogContent` variant that renders in place, so it can be
 * server-rendered (Radix portals mount only after hydration). For
 * non-dismissable dialogs that must paint with the initial HTML.
 *
 * Contract differences from the Radix-backed `AlertDialogContent`:
 * - No `AlertDialog` root: callers own open/close state (render `null` to
 *   close) and must wire `aria-labelledby` / `aria-describedby` to the ids of
 *   {@link StaticAlertDialogTitle} / {@link StaticAlertDialogDescription}.
 * - No modal behavior beyond `aria-modal`: no focus trap, scroll-lock,
 *   `inert`, or dismiss handling — callers own whatever of that their use
 *   case needs.
 */
function StaticAlertDialogContent({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <>
      <div data-slot="alert-dialog-overlay" className={overlayClasses} />
      <div
        role="alertdialog"
        aria-modal="true"
        data-slot="alert-dialog-content"
        className={cn(contentClasses, className)}
        {...props}
      />
    </>
  )
}

/** In-place `AlertDialogTitle` for {@link StaticAlertDialogContent}. */
function StaticAlertDialogTitle({ className, ...props }: React.ComponentProps<'h2'>) {
  return <h2 data-slot="alert-dialog-title" className={cn(titleClasses, className)} {...props} />
}

/** In-place `AlertDialogDescription` for {@link StaticAlertDialogContent}. */
function StaticAlertDialogDescription({ className, ...props }: React.ComponentProps<'p'>) {
  return (
    <p
      data-slot="alert-dialog-description"
      className={cn(descriptionClasses, className)}
      {...props}
    />
  )
}

export {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  AlertDialogPortal,
  AlertDialogTitle,
  AlertDialogTrigger,
  StaticAlertDialogContent,
  StaticAlertDialogDescription,
  StaticAlertDialogTitle
}
